// InfoCard Componen
import {
  HStack,
  Button,
  Select,
  Box,
  Flex,
  Icon,
  Text,
  VStack,
  IconButton,
  Spinner,
  Image,
} from "@chakra-ui/react";
import { useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@chakra-ui/icons";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { FiImage, FiDownload } from "react-icons/fi";
export const InfoCard = ({
  icon,
  label,
  value,
  color = "red",
  colSpan = 1,
}) => (
  <Box
    bg="gray.50"
    borderRadius="lg"
    p={4}
    border="1px solid"
    borderColor="gray.200"
    colSpan={colSpan}
  >
    <Flex align="center" mb={2}>
      <Icon as={icon} color={`${color}.600`} boxSize={5} mr={2} />
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="gray.600"
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {label}
      </Text>
    </Flex>
    <Text fontSize="sm" fontWeight="bold" color="gray.800" isTruncated>
      {value}
    </Text>
  </Box>
);

// ImageCard Component
export const ImageCard = ({
  title,
  subtitle,
  icon,
  imageUrl,
  colorScheme = "red",
}) => {
  const [loading, setLoading] = useState(true);
  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      p={4}
      flex={1}
      width="100%"
      transition="all 0.3s"
      _hover={{
        borderColor: `${colorScheme}.300`,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Flex align="center" mb={3} justify="space-between">
        <Flex align="center">
          <Icon as={icon} color={`${colorScheme}.600`} boxSize={5} mr={2} />
          <Box>
            <Text fontWeight="semibold" color="gray.800">
              {title}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {subtitle}
            </Text>
          </Box>
        </Flex>
        {imageUrl && (
          <IconButton
            aria-label="Download image"
            icon={<FiDownload />}
            colorScheme={colorScheme}
            size="sm"
            variant="solid"
            color="white"
            bg={`${colorScheme}.600`}
            _hover={{ bg: `${colorScheme}.700` }}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const response = await fetch(imageUrl, { mode: 'cors', cache: 'no-cache' });
                if (!response.ok) throw new Error("Network response was not ok");

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${title.replace(/\s+/g, '_').toLowerCase()}.jpg`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.warn("Direct download failed, opening in new tab:", error);
                const link = document.createElement("a");
                link.href = imageUrl;
                link.download = `${title.replace(/\s+/g, '_').toLowerCase()}.jpg`;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }}
          />
        )}
      </Flex>

      <Box
        bg="gray.100"
        borderRadius="md"
        overflow="hidden"
        height="250px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="1px solid"
        borderColor="gray.300"
      >
        {imageUrl ? (
          <>
            {loading && <Spinner size="xl" color={`${colorScheme}.500`} />}
            <Image
              src={imageUrl}
              alt={title}
              objectFit="contain"
              width="100%"
              height="100%"
              transition="transform 0.3s"
              // _hover={{ transform: "scale(1.02)" }}
              _hover={{ transform: "scale(1.02)" }}
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
              display={loading ? "none" : "block"}
            />
          </>
        ) : (
          <VStack spacing={2}>
            <Icon as={FiImage} boxSize={10} color="gray.400" />
            <Text color="gray.500" fontSize="sm" textAlign="center">
              No image available
            </Text>
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export const ProductTable = ({ productSummary }) => {
  const [page, setPage] = useState(1);
  const pageSize = 4;
  const totalPages = Math.ceil(productSummary.length / pageSize);

  const paginatedData = productSummary.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Define AG Grid columns
  const columnDefs = [
    {
      headerName: "Company",
      field: "product.productCompany.productCompanyName",
      valueGetter: (params) => params.data.product.productCompany.productCompanyName,
      flex: 1.2,


    },
    {
      headerName: "Brand",
      field: "product.productBrand.productBrandName",
      valueGetter: (params) => params.data.product.productBrand.productBrandName,
      flex: 0.7,


    },
    {
      headerName: "Pack Type",
      field: "product.packType",
      valueGetter: (params) => params.data.product.packType,
      flex: 0.7,


    },
    {
      headerName: "Pack Size",
      field: "product.packSize",
      valueGetter: (params) => params.data.product.packSize,
      flex: 0.7,


    },
    {
      headerName: "Product Name",
      field: "product.productName",
      valueGetter: (params) => params.data.product.productName,
      flex: 1.2,

    },
    {
      headerName: "Count",
      field: "productCount",
      flex: 0.7,

    },
  ];

  // Default column properties
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  return (
    <Box>
      {/* AG Grid Table */}
      <Box
        className="ag-theme-alpine"
        height="auto"
        sx={{
          '.ag-root-wrapper': {
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
          },
          '.ag-header': {
            backgroundColor: '#FED7D7',
            borderBottom: '2px solid #FC8181',
            alignItems: "center",
            textAlign: "center",
          },
          '.ag-header-cell': {
            fontSize: '14px',
            fontWeight: '600',
            color: '#2D3748',
            borderRight: '1px solid #FC8181',
          },
          '.ag-header-cell:last-child': {
            borderRight: 'none',
          },
          '.ag-header-cell-label': {
            justifyContent: 'center',
          },
          '.ag-cell': {
            fontSize: '14px',
            padding: "12px",
            color: '#4A5568',
            textAlign: "center",
            borderRight: '1px solid #E2E8F0',
          },
          '.ag-cell:last-child': {
            borderRight: 'none',
          },
          '.ag-row': {
            borderBottom: '1px solid #E2E8F0',
          },
          '.ag-row:hover': {
            backgroundColor: '#FFF5F5',
          },
        }}
      >
        <AgGridReact
          rowData={paginatedData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="autoHeight"
          suppressPaginationPanel={true}
          suppressDragLeaveHidesColumns={true}
          suppressCellFocus={true}
          rowHeight={48}
          headerHeight={48}
          suppressMovableColumns={true}
        />
      </Box>

      {/* Custom Pagination Footer */}
      <Flex justify="space-between" align="center" mt={3} pt={3} borderTop="1px solid" borderColor="gray.200">
        <Text fontSize="sm" color="gray.600">
          Showing {(page - 1) * pageSize + 1} to{" "}
          {Math.min(page * pageSize, productSummary.length)} of{" "}
          {productSummary.length} entries
        </Text>

        <HStack spacing={2}>
          <Button
            size="sm"
            onClick={() => setPage(1)}
            isDisabled={page === 1}
            leftIcon={<ArrowLeftIcon />}
            variant="outline"
            colorScheme="gray"
          >
            First
          </Button>
          <Button
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            isDisabled={page === 1}
            leftIcon={<ChevronLeftIcon />}
            variant="outline"
            colorScheme="gray"
          >
            Previous
          </Button>

          <Select
            size="sm"
            width="140px"
            value={page}
            onChange={(e) => setPage(Number(e.target.value))}
            variant="outline"
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                Page {i + 1} of {totalPages}
              </option>
            ))}
          </Select>

          <Button
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            isDisabled={page === totalPages}
            rightIcon={<ChevronRightIcon />}
            variant="outline"
            colorScheme="gray"
          >
            Next
          </Button>
          <Button
            size="sm"
            onClick={() => setPage(totalPages)}
            isDisabled={page === totalPages}
            rightIcon={<ArrowRightIcon />}
            variant="outline"
            colorScheme="gray"
          >
            Last
          </Button>
        </HStack>
      </Flex>
    </Box>
  );
};